import { useEffect, useRef } from "react";
import { useApi } from "@excellent/ui/api";
import { objects } from "@excellent/ui/api/contracts/objects";
import { Icon } from "./icons";
import { TaskInspectorProof } from "./task-inspector-proof";
import { verificationCanonicalClients } from "./api/canonical-clients";
import { useVerificationEnvOptional } from "./env-provider";
import {
  isVerificationInspectorUid,
  parseVerificationInspectorUid,
  projectArtifact,
  projectControl,
  projectPack,
  projectReceipt,
  projectWorkspaceObject,
  type InspectorObject,
} from "./inspector-object";

export function ObjectInspectorPane({
  uid,
  onClose,
  onNavigate,
  focusOnMount,
  onTitleChange,
  fallbackTitle,
}: {
  uid: string;
  onClose: () => void;
  onNavigate: (to: string) => void;
  focusOnMount: boolean;
  onTitleChange: (title: string) => void;
  fallbackTitle: string;
}) {
  const env = useVerificationEnvOptional();
  const verificationRef = parseVerificationInspectorUid(uid);
  const invalidVerificationRef = isVerificationInspectorUid(uid) && !verificationRef;
  const environmentPending = Boolean(verificationRef && (!env || !env.ready));
  const environmentMatches = Boolean(
    verificationRef && env?.ready && env.environment === verificationRef.environment,
  );
  const workspaceObjectApi = useApi(
    objects.get,
    verificationRef || invalidVerificationRef ? null : { params: { uid } },
  );
  const controlApi = useApi(
    verificationCanonicalClients.controlsGet.operation,
    verificationRef?.kind === "control" && environmentMatches
      ? { params: { id: verificationRef.id }, query: { env: verificationRef.environment } }
      : null,
  );
  const artifactApi = useApi(
    verificationCanonicalClients.artifactsGet.operation,
    verificationRef?.kind === "artifact" && environmentMatches
      ? { params: { id: verificationRef.id }, query: { env: verificationRef.environment } }
      : null,
  );
  const lineageApi = useApi(
    verificationCanonicalClients.lineageGet.operation,
    verificationRef?.kind === "artifact" && environmentMatches
      ? { params: { id: verificationRef.id }, query: { env: verificationRef.environment } }
      : null,
  );
  const receiptApi = useApi(
    verificationCanonicalClients.receiptsGet.operation,
    verificationRef?.kind === "receipt" && environmentMatches
      ? { params: { id: verificationRef.id }, query: { env: verificationRef.environment } }
      : null,
  );
  const packApi = useApi(
    verificationCanonicalClients.packsGet.operation,
    verificationRef?.kind === "pack" && environmentMatches
      ? { params: { id: verificationRef.id }, query: { env: verificationRef.environment } }
      : null,
  );

  let object: InspectorObject | null = null;
  let error: unknown = null;
  const resolvedWorkspaceObject =
    workspaceObjectApi.data?.uid === uid ? workspaceObjectApi.data : null;
  if (!verificationRef) {
    object = resolvedWorkspaceObject ? projectWorkspaceObject(resolvedWorkspaceObject) : null;
    error = workspaceObjectApi.error;
  } else if (environmentMatches) {
    if (verificationRef.kind === "control") {
      object = controlApi.data
        ? projectControl(controlApi.data.control, verificationRef.environment)
        : null;
      error = controlApi.error;
    } else if (verificationRef.kind === "artifact") {
      object = artifactApi.data
        ? projectArtifact(
            artifactApi.data.artifact,
            verificationRef.environment,
            lineageApi.data?.entries,
          )
        : null;
      error = artifactApi.error;
    } else if (verificationRef.kind === "receipt") {
      object = receiptApi.data
        ? projectReceipt(receiptApi.data.receipt, verificationRef.environment)
        : null;
      error = receiptApi.error;
    } else {
      object = packApi.data
        ? projectPack(packApi.data.pack, verificationRef.environment)
        : null;
      error = packApi.error;
    }
  }
  const headingRef = useRef<HTMLHeadingElement>(null);
  const typedFallbackTitle = verificationRef
    ? `${verificationRef.kind === "artifact" ? "Evidence" : verificationRef.kind[0]?.toUpperCase() + verificationRef.kind.slice(1)} ${verificationRef.id}`
    : fallbackTitle;
  const title = object?.title ?? typedFallbackTitle;
  const fullPageHref = object?.href ?? null;
  const activityTitle = object?.title ?? null;
  const taskId =
    !verificationRef &&
    !invalidVerificationRef &&
    resolvedWorkspaceObject?.kind === "task" &&
    resolvedWorkspaceObject.localId != null &&
    Number.isSafeInteger(resolvedWorkspaceObject.localId) &&
    resolvedWorkspaceObject.localId > 0
      ? resolvedWorkspaceObject.localId
      : null;

  useEffect(() => {
    if (!focusOnMount) return;
    const frame = window.requestAnimationFrame(() => headingRef.current?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, [focusOnMount, uid]);

  useEffect(() => {
    if (object?.title) onTitleChange(object.title);
  }, [object?.title, onTitleChange]);

  return (
    <aside
      id="vp-inspector-pane"
      className="vp-pane vp-inspector-pane"
      data-pane="inspector"
      data-inspector-resource-kind={verificationRef?.kind ?? "workspace-object"}
      aria-labelledby="vp-inspector-title"
      onKeyDown={(event) => {
        if (event.key !== "Escape" || event.defaultPrevented) return;
        event.preventDefault();
        onClose();
      }}
    >
      <header className="vp-pane-header">
        <span className="vp-pane-header-icon" aria-hidden="true"><Icon name="book" size={14} /></span>
        <h2 id="vp-inspector-title" ref={headingRef} tabIndex={-1}>{title}</h2>
        <span className="vp-pane-spacer" />
        <button type="button" className="vp-pane-icon" data-action-id="inspector.close" onClick={onClose} aria-label={`Close ${title}`} title={`Close ${title}`}>
          <Icon name="x" size={14} />
        </button>
      </header>

      <div className="vp-pane-scroll vp-inspector-scroll">
        {invalidVerificationRef ? (
          <div className="vp-inspector-empty" role="status">
            <strong>This inspection link is invalid.</strong>
            <p>No record was requested.</p>
          </div>
        ) : environmentPending ? (
          <div className="vp-inspector-empty" role="status">
            <strong>Loading environment</strong>
            <p>Waiting for the active verification environment before reading this record.</p>
          </div>
        ) : verificationRef && !environmentMatches ? (
          <div className="vp-inspector-empty" role="status">
            <strong>This record belongs to {verificationRef.environment === "demo" ? "Demo" : "Real"}.</strong>
            <p>Switch to that environment before loading its details.</p>
          </div>
        ) : error ? (
          <div className="vp-inspector-empty" role="status">
            <strong>This record could not be loaded.</strong>
            <p>It may have been removed, or this account may not have access.</p>
          </div>
        ) : !object ? (
          <div className="vp-inspector-empty" role="status">
            <strong>Loading details</strong>
            <p>Reading the selected object from this workspace.</p>
          </div>
        ) : (
          <>
            <section className="vp-inspector-section" aria-labelledby="vp-inspector-basis">
              <span>{object.kind}</span>
              <h3 id="vp-inspector-basis">Basis</h3>
              <p>{object.description}</p>
              <dl className="vp-inspector-facts">
                <div><dt>Status</dt><dd>{object.state.replace(/_/g, " ")}</dd></div>
                {object.facts.map((fact) => <div key={fact.label}><dt>{fact.label}</dt><dd>{fact.value}</dd></div>)}
              </dl>
              <small>Limitation: {object.limitation}</small>
            </section>

            <section className="vp-inspector-section" aria-labelledby="vp-inspector-observations">
              <div className="vp-inspector-section-title">
                <h3 id="vp-inspector-observations">Activity</h3>
                <span>{object.observations.length}</span>
              </div>
              <p><strong>Evidence and observations</strong></p>
              <p>{object.observationsSummary}</p>
              {object.timestamps.length ? (
                <dl className="vp-inspector-facts">
                  {object.timestamps.map((timestamp) => (
                    <div key={timestamp.label}>
                      <dt>{timestamp.label}</dt>
                      <dd><time dateTime={timestamp.value}>{new Date(timestamp.value).toLocaleString()}</time></dd>
                    </div>
                  ))}
                </dl>
              ) : null}
              {object.observations.length ? (
                <ol className="vp-inspector-events">
                  {object.observations.slice(0, 6).map((event) => (
                    <li key={event.id}>
                      <strong>{event.label}</strong>
                      {event.summary ? <p>{event.summary}</p> : null}
                      {event.actor || event.occurredAt ? (
                        <small>
                          {event.actor ?? "Actor unavailable"}
                          {event.occurredAt ? (
                            <> · <time dateTime={event.occurredAt}>{new Date(event.occurredAt).toLocaleString()}</time></>
                          ) : null}
                        </small>
                      ) : null}
                    </li>
                  ))}
                </ol>
              ) : <p className="vp-inspector-muted">No detailed observations were returned.</p>}
            </section>

            {taskId != null ? <TaskInspectorProof key={taskId} taskId={taskId} /> : null}

            <details className="vp-inspector-technical">
              <summary>How to interpret this</summary>
              <dl className="vp-inspector-facts">
                {object.interpretation.map((fact) => <div key={fact.label}><dt>{fact.label}</dt><dd>{fact.value}</dd></div>)}
              </dl>
            </details>

            <details className="vp-inspector-technical">
              <summary>Technical details</summary>
              <p><strong>Telemetry and diagnostics</strong></p>
              <dl className="vp-inspector-facts">
                {object.technical.map((fact) => <div key={fact.label}><dt>{fact.label}</dt><dd><code>{fact.value}</code></dd></div>)}
              </dl>
              {object.observations.length ? (
                <details>
                  <summary>Observation identifiers</summary>
                  <ul className="vp-inspector-events">
                    {object.observations.slice(0, 6).map((event) => (
                      <li key={event.id}><code>{event.id}</code></li>
                    ))}
                  </ul>
                </details>
              ) : null}
            </details>

            <div className="vp-inspector-actions">
              <button type="button" data-action-id="inspector.open-full-page" onClick={() => fullPageHref && onNavigate(fullPageHref)}>Open full page</button>
              <button type="button" data-action-id="inspector.find-related-activity" onClick={() => activityTitle && onNavigate(`/activity?q=${encodeURIComponent(activityTitle)}`)}>Find related activity</button>
            </div>
          </>
        )}
      </div>
    </aside>
  );
}
